import { useTranslation } from 'react-i18next';

import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
import { Brain } from '@/shared/types/brain';
import { Campaign } from '@/shared/types/campaigns';

interface Props {
  campaign?: Campaign;
  brain?: Brain;
}

/**
 * "Campaign Guidelines" section — only meaningful for campaigns with autonomous
 * actions enabled (`is_agentic`), where the AI agent guidelines live in
 * `nba_config.guidelines`. Renders the guidelines markdown read-only.
 */
export const CampaignGuidelinesSummary = ({ campaign, brain }: Props) => {
  const { t } = useTranslation();
  const guidelines = campaign?.nba_config?.guidelines ?? '';
  const { html } = useMarkdownToHtml(guidelines);

  if (!campaign?.is_agentic || !guidelines) {
    return null;
  }

  return (
    <section className="mt-8 px-6">
      <h2 className="mb-6 text-lg leading-none font-semibold text-foreground">
        {t('campaigns.analytics.guidelines_title')}
      </h2>
      <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
        <div className="w-40 shrink-0">
          <p className="text-sm leading-none font-medium text-foreground">
            {brain?.name || t('campaigns.not_assigned')}
          </p>
          {brain?.name && campaign?.brain_version != null && (
            <p className="mt-1 text-sm text-muted-foreground">v{campaign.brain_version}</p>
          )}
        </div>
        <div
          className="flex-1 text-sm leading-6 font-medium text-foreground [&_a]:text-primary [&_a]:underline [&_li]:font-medium [&_p]:font-medium"
          // markdownToHtml sanitizes its output before returning.
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </section>
  );
};
